/* eslint-disable @typescript-eslint/no-explicit-any */
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import React from 'react';

/**
 * It takes a list of children, and returns a list of children that can be reordered
 * @param {any} result - The result of the drag and drop operation.
 * @returns A function that takes a single argument, `provided`, and returns a `div` element.
 */
interface Props {
  onReorder: (newItems: React.ReactNode[]) => void;
  children: React.ReactNode;
  itemsId?: string;
  zIndex?: number;
  droppableId?: string;
  isDragDisabled?: boolean;
}

export const SortableProvider: React.FC<Props> = ({
  onReorder,
  children,
  droppableId = 'items',
  itemsId = '_id',
  zIndex = 1,
  isDragDisabled = false
}) => {
  const items =
    React.Children.map(children, (child, _i) => ({
      _id: (child as any).props[itemsId],
      content: child
    })) ?? [];

  function handleDragEnd(result: any) {
    if (!result.destination) return;
    const newItems = Array.from(items);
    const [reorderedItem] = newItems.splice(result.source.index, 1);
    newItems.splice(result.destination.index, 0, reorderedItem);
    onReorder(newItems.map(item => item.content));
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId={droppableId}>
        {provided => (
          <div {...provided.droppableProps} ref={provided.innerRef}>
            {items.map((item, index) => (
              <Draggable
                index={index}
                key={item._id}
                draggableId={item._id}
                isDragDisabled={isDragDisabled}>
                {providedItemData => (
                  <div
                    style={{ zIndex }}
                    ref={providedItemData.innerRef}
                    {...providedItemData.draggableProps}
                    {...providedItemData.dragHandleProps}>
                    {item.content}
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};
